import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [180, -180]);
  const bgOpacity = useTransform(
    scrollYProgress,
    [0, 0.25, 0.75, 1],
    [0, 0.05, 0.05, 0]
  );
  const headingY = useTransform(scrollYProgress, [0, 1], [50, -30]);
  const bodyY = useTransform(scrollYProgress, [0, 1], [80, -50]);

  return (
    <section
      ref={ref}
      data-testid="manifesto-section"
      data-snap-page
      className="relative bg-surface h-[100svh] flex items-center overflow-hidden"
    >
      <motion.div
        aria-hidden
        style={{ y: bgY, opacity: bgOpacity }}
        className="pointer-events-none absolute left-[-3vw] top-1/2 -translate-y-1/2 font-display text-[28vw] md:text-[18vw] leading-none tracking-[-0.06em] text-ink select-none"
      >
        approach.
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-12 gap-x-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1, ease }}
            className="col-span-12 md:col-span-3"
          >
            <p className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2">
              01 — Approach
            </p>
          </motion.div>

          <div className="col-span-12 md:col-span-9 md:col-start-4">
            <motion.div style={{ y: headingY }}>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 1, ease, delay: 0.1 }}
                className="font-display text-3xl md:text-5xl lg:text-[56px] leading-[1.1] tracking-[-0.03em] text-ink max-w-[24ch]"
              >
                I care about impact — and I love turning ideas into reality, fast.
              </motion.h2>
            </motion.div>

            <motion.div style={{ y: bodyY }} className="mt-10">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 1, ease, delay: 0.25 }}
                className="grid md:grid-cols-2 gap-x-12 gap-y-6 font-body text-base md:text-lg text-ink-2 leading-relaxed max-w-3xl"
              >
                <p>
                  Every project starts with a simple question: will this actually help people
                  work better? I care deeply about outcomes — and I&apos;m genuinely excited to
                  try new tools, patterns, and AI capabilities when they can move the needle for
                  an organization.
                </p>
                <p>
                  My sweet spot is the gap between a rough idea and something live in production.
                  I enjoy that sprint — prototyping quickly, learning in the open, iterating with
                  stakeholders — and shipping solutions teams adopt because they solve real
                  problems, not slide-deck problems.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
